import { formatShortDateTime, formatTime, formatWeekdayName } from "@clipboard-health/date-time";
import { addDays, isSameDay, startOfDay } from "date-fns";

/**
 * Formats the next penalty increase date for display in the cancellation penalty footer.
 *
 * Rules:
 * - If the date is today: "today, HH:MM AM/PM"
 * - If the date is tomorrow: "tomorrow, HH:MM AM/PM"
 * - If the date is within 7 days: "DayOfWeek, HH:MM AM/PM" (e.g., "Wednesday, 2:00 PM")
 * - Otherwise: "MMM D, HH:MM AM/PM" (e.g., "Jan 25, 9:00 AM")
 *
 * @param date - The date to format
 * @param currentDate - The reference "now" date, used for today/tomorrow/7-day comparisons.
 *   Should be sourced from `useCurrentDate` so it stays in sync with the refresh timer.
 */
export function formatNextIncreaseDate(date: Date, currentDate: Date): string {
  if (isSameDay(date, currentDate)) {
    return `today, ${formatTime(date)}`;
  }

  const tomorrow = addDays(startOfDay(currentDate), 1);
  if (isSameDay(date, tomorrow)) {
    return `tomorrow, ${formatTime(date)}`;
  }

  const sevenDaysFromNow = addDays(startOfDay(currentDate), 7);

  if (date < sevenDaysFromNow) {
    return `${formatWeekdayName(date)}, ${formatTime(date)}`;
  }

  return formatShortDateTime(date);
}
